import {
    Component,
    OnDestroy,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule, UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import {
    BehaviorSubject,
    Observable,
    Subject,
    firstValueFrom,
    from
} from "rxjs";
import { map, takeUntil } from "rxjs/operators";
import { BusyService } from "~app/common/components/busy/busy.service";
import { IMessageModel, MessageSeverity } from "~app/common/components/message-panel/message-panel.models";
import { RaskColumn, RaskColumnButtonTypes } from "~app/common/components/table/models/rask-column";
import { RaskDataChanged } from "~app/common/components/table/models/rask-data-changed-typed";
import { RaskRoller } from "~app/common/models/constants";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { CommandService } from "~app/common/services/cqs/command.service";
import { CommandManager, ICommandManager } from "~app/common/services/cqs/commandManager";
import { EventService } from "~app/common/services/cqs/event.service";

import { AsyncPipe } from "@angular/common";
import { BusySpinnerComponent } from "@components/busy/busy-spinner/busy-spinner.component";
import { ButtonComponent } from "@components/button/button.component";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { InputComponent } from "@components/input/input.component";
import { MessagePanelComponent } from "@components/message-panel/message-panel.component";
import { MultiSelectorComponent } from "@components/multi-selector/multi-selector.component";
import { TableComponent } from "@components/table/table.component";
import {
    InitiateArbetsorderViol3SynchronizationCommand
} from "~app/features/administration/systemadministration/tabs/sysadm-viol3/commands/InitiateArbetsorderViol3SynchronizationCommand";
import { HandelssortimentService } from "~app/features/forsaljning/handelssortiment/handelssortiment.service";
import { IRedovisningshanvisningDto } from "~app/features/planera/model/IRedovisningshanvisningDto";
import { InmatningshandelssortimentRedovisningshanvisningDto, Viol3DestineringsraderDto } from "~app/features/planera/model/IViol3DestineringDto";
import { Viol3formatService } from "~app/features/shared/services/viol3format.service";
import { SetDestineringActiveCommand } from "~app/features/transport/transportplanera/models/commands";
import { DestineringsalternativViewModel } from "../arbetsorder-destineringsalternativ/arbetsorder-destineringsalternativ.models";
import { PushForwardRedovisningshanvisningGiltighetCommand } from "../commands/PushForwardRedovisningshanvisningGiltighetCommand";
import { RemoveRedovisningshanvisningGiltighetCommand } from "../commands/RemoveRedovisningshanvisningGiltighetCommand";
import { SetJusteradRedovisningshanvisningCommand } from "../commands/SetJusteradRedovisningshanvisningCommand";
import { IViol3JusteraRow, Viol3DestineringViewModel } from "../model/Viol3DestineringViewModel";
import { ArbetsorderService } from "../services/arbetsorder.service";
import { Viol3DestineringRowViewModel } from "./arbetsorder-viol3-destineringsrader.component.models";

@Component({
    selector: "rask-arbetsorder-viol3-destineringsrader",
    templateUrl: "./arbetsorder-viol3-destineringsrader.component.html",
    styleUrls: ["./arbetsorder-viol3-destineringsrader.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        MessagePanelComponent,
        InputComponent,
        TableComponent,
        MultiSelectorComponent,
        FormsModule,
        CheckboxComponent,
        BusySpinnerComponent,
        ButtonComponent,
        AsyncPipe
    ]
})
export class ArbetsorderViol3DestineringsraderComponent implements OnInit, OnDestroy {
    activeModal = inject(NgbActiveModal);
    boundAktivFlag: boolean;
    data: {
        arbetsorderId: any;
        alternativ: DestineringsalternativViewModel;
        editable: boolean;
    };
    destineringsursprung: string;
    form = new UntypedFormGroup({
        justeradRedovisningshanvisning: new UntypedFormControl([])
    });
    formDirty: boolean;
    initialAktivFlag: boolean;
    isLoading = false;
    isRoleSystemadmin = false;
    justeraTableColumns: RaskColumn[];
    messages$: BehaviorSubject<IMessageModel[]> = new BehaviorSubject<IMessageModel[]>([]);
    messageItemsAsObservable: Observable<IMessageModel[]> = this.messages$.asObservable();
    tableColumns: RaskColumn[];
    viewModel: Viol3DestineringViewModel;

    private readonly arbetsorderService = inject(ArbetsorderService);
    private readonly authenticationService = inject(AuthenticationService);
    private readonly busyService = inject(BusyService);
    private readonly commandManager: ICommandManager;
    private readonly commandService = inject(CommandService);
    private readonly eventService = inject(EventService);
    private readonly handelssortimentService = inject(HandelssortimentService);
    private readonly ngUnsubscribe$ = new Subject<void>();

    constructor() {
        this.commandManager = new CommandManager();

        this.isRoleSystemadmin = this.authenticationService.hasRole(RaskRoller.role.Arbetsordersystemadmin);
    }

    get canEdit(): boolean {
        return this.data.editable;
    }

    addOrUpdateAktivDestineringsalternativActive() {
        const command = new SetDestineringActiveCommand(this.data.alternativ.id, this.boundAktivFlag);
        this.commandManager.updateCommand(command);

        if (this.initialAktivFlag === this.boundAktivFlag) {
            this.commandManager.remove(command);
        }
    }

    canSave() {
        return this.commandManager.isDirty();
    }

    close(): void {
        this.activeModal.close();
    }

    extraInmatningshandelssortimentFormatter = result => result ? `${result.namn} (${result.violId})` : undefined;

    extraInmatningshandelssortimentModified() {
        this.viewModel.processExtraInmatningshandelssortimentChanges(this.commandManager);
    }

    extraInmatningshandelssortimentSelectedFormatter = result => result ? `${result.violId}` : undefined;

    initData() {
        this.isLoading = true;
        this.initialAktivFlag = this.data.alternativ.aktiv;
        this.boundAktivFlag = this.initialAktivFlag;
        this.reloadData();

        this.initColumns();
        this.destineringsursprung = this.data.alternativ.formattedDestineringsursprung;
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    ngOnInit(): void {
        this.initData();

        this.eventService.subscribeAllArbetsorderSaga("SynchronizeVirkesorderSaga")
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe((event) => {
                if (event.active === false) {
                    this.reloadData();
                }
            });
    }

    onFetchViol3Destineringsrader(input: Viol3DestineringsraderDto) {
        const mplTitle = `${this.data.alternativ.mottagningsplatsNamn}  ${this.data.alternativ.mottagningsplatsnummer}`;

        const messages = input.destineringstyper
            .flatMap(x => x.inmatningshandelssortimentRedovisningshanvisningar)
            .map(x => x.arbetsorderRedovisningshanvisning?.relateradeDestineringar
                .map(rel => ({ relateradDestinering: rel,
                    inmatningshandelssortiment: x.arbetsorderRedovisningshanvisning.inmatningshandelssortiment })))
            .reduce((prev, curr) => prev.concat(curr), [])
            ?.filter(p => p.relateradDestinering.destineringUid !== this.data.alternativ.destineringUid)
            .map(dest => {
                const hsTitle = dest.relateradDestinering.handelssortimentNamn +
                    (dest.relateradDestinering.foradlatHandelssortimentNamn != null ? `/${dest.relateradDestinering.foradlatHandelssortimentNamn}` : "");
                return {
                    severity: MessageSeverity.Info,
                    message: `De ändringar som görs här för inmätningshandelssortiment \
${dest.inmatningshandelssortiment} kommer även att slå igenom på handelssortiment för ${hsTitle} till ${mplTitle}`
                } as IMessageModel;
            })
            .reduce((prev, curr) => prev.concat(curr), []);
        this.messages$.next(messages || []);
    }

    redovisningshanvisningFormatter = result => result ? result.identitet : undefined;

    redovisningshanvisningModified(inmatningshandelssortiment: string, mottagningsplatsnummer: string, redovisningshanvisning: string, typ: string) {
        this.commandManager.addOrUpdate(new SetJusteradRedovisningshanvisningCommand(c => {
            c.arbetsorderId = this.data.arbetsorderId;
            c.redovisningshanvisning = redovisningshanvisning;
            c.inmatningshandelssortiment = inmatningshandelssortiment;
            c.mottagningsplatsnummer = mottagningsplatsnummer;
            c.typ = typ;
        }), c => c.$type === SetJusteradRedovisningshanvisningCommand.$type);
    }

    redovisningshanvisningViewFormatter = result => result?.identitet ? `${result.identitet} - ${result.namn}` : undefined;

    searchForInmatningshandelssortiment = (searchString: string) => this.handelssortimentService
        .searchForHandelssortiment({
            forInmatning: true,
            wildcardSearch: searchString
        }).pipe(
            map((response: any[]) => response.filter((item: any) => !this.viewModel.extrainmatningshandelssortiment.some(violId => item.violId === violId)))
        );

    spara() {
        // Se till att spegling alltid startar
        const command = new InitiateArbetsorderViol3SynchronizationCommand(x => x.arbetsorderId = this.data.arbetsorderId);
        this.commandManager.addOrUpdate(command, c => c.$type === InitiateArbetsorderViol3SynchronizationCommand.$type);

        const work = from(this.commandService.pushCommands(this.commandManager));
        const obs = this.busyService
            .addWork(work);

        obs.subscribe(() => {
            this.initialAktivFlag = this.boundAktivFlag;
            this.data.alternativ.aktiv = this.boundAktivFlag;
            this.form.markAsPristine();
            this.commandManager.clear();
            this.isLoading = true;
        });
        return firstValueFrom(obs);
    }

    private hasOmdestinering(rh: InmatningshandelssortimentRedovisningshanvisningDto) {
        return rh.viol3Destineringsrader.some(x => !x.skickad) && rh.arbetsorderRedovisningshanvisning.planeratGiltigFrom;
    }

    private initColumns() {
        const rhChanged = new Subject<RaskDataChanged<InmatningshandelssortimentRedovisningshanvisningDto, IRedovisningshanvisningDto>>();
        rhChanged.pipe(
            takeUntil(this.ngUnsubscribe$)
        ).subscribe(x => {
            this.redovisningshanvisningModified(x.model.inmatningshandelssortiment,
                this.data.alternativ.mottagningsplatsnummer,
                x.value?.identitet,
                x.model.arbetsorderRedovisningshanvisning.typ);
        });

        this.justeraTableColumns = [
            {
                headerName: "Inmätt hs",
                field: "inmatningshandelssortiment",
                filter: false,
                sortable: false,
                minWidth: 70,
                type: "span"
            },
            {
                headerName: "Redovisningshänvisning",
                field: "redovisningshanvisning",
                filter: false,
                sortable: false,
                tooltipGenerator: (row: IViol3JusteraRow, rh: IRedovisningshanvisningDto) =>
                    Viol3formatService.formatRedovisninghanvisningTooltip(rh),
                minWidth: 140,
                type: "selector",
                editable: this.canEdit,
                values: (args: IViol3JusteraRow) => args.applicableRedovisningshanvisningar,
                viewFormatter: (arg) => arg ? `${arg.identitet}: ${arg.namn}` : undefined,
                formatter: (arg) => arg,
                dataChanged: rhChanged
            },
            {
                headerName: "Omdestinera",
                editable: false,
                field: " ",
                type: "button",
                onClick: (rh: IViol3JusteraRow) => this.hasOmdestinering(rh) ?
                    this.removeGiltighet(rh.inmatningshandelssortiment, rh.typ) : this.pushForward(rh.inmatningshandelssortiment, rh.typ),
                filter: false,
                text: "",
                minWidth: 120,
                maxWidth: 120,
                buttonType: (rh: IViol3JusteraRow) => this.hasOmdestinering(rh) ? RaskColumnButtonTypes.delete : RaskColumnButtonTypes.forward,
                hide: !this.isRoleSystemadmin
            }
        ] as any[] as RaskColumn[];

        this.tableColumns = [
            {
                headerName: "Inmätt hs",
                field: "inmatningshandelssortiment",
                filter: false,
                sortable: false,
                minWidth: 70,
                type: "span"
            },
            {
                headerName: "Producerat hs",
                field: "produceratHandelssortiment",
                filter: false,
                sortable: false,
                minWidth: 70,
                type: "span"
            },
            {
                headerName: "Redovisningshänvisning",
                field: "redovisningshanvisningIdentitet",
                filter: false,
                sortable: false,
                tooltipGenerator: (row: Viol3DestineringRowViewModel, value: string) =>
                    Viol3formatService.formatRedovisninghanvisningTooltip(row.sourceDto.redovisningshanvisning),
                minWidth: 140,
                type: "span"
            },
            {
                headerName: "Giltig from",
                field: "giltigFrom",
                filter: false,
                sortable: false,
                minWidth: 100,
                type: "span",
                dataChanged: new Subject()
            },
            {
                headerName: "Giltig tom",
                field: "giltigTom",
                filter: false,
                sortable: false,
                minWidth: 100,
                type: "span",
                dataChanged: new Subject()
            },
            {
                headerName: "Skickad",
                field: "skickadTxt",
                filter: false,
                sortable: false,
                minWidth: 100,
                type: "span",
                dataChanged: new Subject()
            }
        ] as any[] as RaskColumn[];
    }

    private initGiltighetCommand(inmatningshandelssortiment: string, typ?: string):
        (instance: RemoveRedovisningshanvisningGiltighetCommand | PushForwardRedovisningshanvisningGiltighetCommand) => void {
        return (c) => {
            c.arbetsorderId = this.data.arbetsorderId;
            c.inmatningshandelssortiment = inmatningshandelssortiment;
            c.mottagningsplatsnummer = this.data.alternativ.mottagningsplatsnummer;
            c.typ = typ;
        };
    }

    private async pushForward(inmatningshandelssortiment: string, typ?: string) {
        this.commandManager.updateCommand(new PushForwardRedovisningshanvisningGiltighetCommand(this.initGiltighetCommand(inmatningshandelssortiment, typ)));
        await this.spara();
    }

    private reloadData() {
        return this.arbetsorderService.getViol3Destineringsrader(this.data.arbetsorderId, this.data.alternativ.id).then(x => {
            this.onFetchViol3Destineringsrader(x);
            this.viewModel = new Viol3DestineringViewModel(x.destineringstyper, x.extraInmatningshandelssortiment, this.data.alternativ.id);
            this.isLoading = false;
        });
    }

    private async removeGiltighet(inmatningshandelssortiment: string, typ?: string) {
        this.commandManager.updateCommand(new RemoveRedovisningshanvisningGiltighetCommand(this.initGiltighetCommand(inmatningshandelssortiment, typ)));
        await this.spara();
    }
}
